import { CATEGORY_DISPLAY } from '@opensentry/shared';
import { useState } from 'react';
import { useEvent } from '../hooks/useData.js';
import { api } from '../lib/api.js';
import { localTime, timeAgo } from '../lib/format.js';
import { CategoryIcon } from './CategoryIcon.js';
import { ExplainWhy } from './ExplainWhy.js';
import { SeverityPill } from './SeverityPill.js';
import { Button } from './ui/Button.js';
import { Drawer } from './ui/Drawer.js';

export function EventDrawer({
  eventId,
  onClose,
}: {
  eventId: number | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useEvent(eventId);
  // Keyed by event id so the prompt resets when a different event opens.
  const [given, setGiven] = useState<{ id: number; relevant: boolean } | null>(null);
  const feedback = given && given.id === eventId ? given.relevant : null;

  return (
    <Drawer open={eventId != null} onClose={onClose} title="Event">
      {isLoading || !data ? (
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
            <div className="flex items-center gap-2 bg-[var(--color-panel-2)] px-3 py-2">
              <CategoryIcon category={data.event.category} />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                {CATEGORY_DISPLAY[data.event.category].label}
              </span>
              <span className="ml-auto">
                <SeverityPill severity={data.event.severity} />
              </span>
            </div>
            <div className="px-3 py-2">
              <h2 className="text-base font-semibold leading-snug">{data.event.title}</h2>
              <div className="mono mt-1 text-[11px] text-[var(--color-muted)]">
                {data.event.lat.toFixed(2)}, {data.event.lng.toFixed(2)} ·{' '}
                {localTime(data.event.occurredAt, data.event.lng)} ·{' '}
                {timeAgo(data.event.occurredAt)}
              </div>
            </div>
            <div className="grid grid-cols-2 divide-x divide-[var(--color-border)] border-t border-[var(--color-border)] bg-[var(--color-panel-2)]">
              <div className="px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  Zones affected
                </div>
                <div className="mono text-lg font-semibold leading-tight">
                  {data.affectedZones.length}
                </div>
              </div>
              <div className="px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  People affected
                </div>
                <div className="mono text-lg font-semibold leading-tight">
                  {data.affectedZones.reduce((s, z) => s + (z.zone.employeeCount ?? 0), 0)}
                </div>
              </div>
            </div>
          </div>
          {data.event.description && (
            <p className="text-sm text-[var(--color-muted)]">{data.event.description}</p>
          )}
          <div className="flex items-center gap-3 text-[11px] text-[var(--color-muted)]">
            <span className="uppercase">{data.event.source}</span>
            {data.event.url && (
              <a href={data.event.url} target="_blank" rel="noreferrer" className="underline">
                open source
              </a>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">
              Affected zones ({data.affectedZones.length})
            </h3>
            <ul className="space-y-1 text-xs">
              {data.affectedZones.map((z) => (
                <li
                  key={z.zone.id}
                  className="rounded border border-[var(--color-border)] px-2 py-1"
                >
                  <div className="flex justify-between">
                    <span>{z.zone.label ?? `Zone ${z.zone.id}`}</span>
                    <span className="mono text-[var(--color-muted)]">
                      {z.zone.employeeCount} ppl · {z.distanceKm.toFixed(0)} km
                    </span>
                  </div>
                  <ExplainWhy eventId={data.event.id} zoneId={z.zone.id} />
                </li>
              ))}
              {data.affectedZones.length === 0 && (
                <li className="text-[var(--color-muted)]">No workforce zones in range.</li>
              )}
            </ul>
          </div>

          <div className="border-t border-[var(--color-border)] pt-3">
            {feedback == null ? (
              <>
                <div className="mb-2 text-xs text-[var(--color-muted)]">
                  Is this event relevant to your workforce?
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={async () => {
                      await api.feedback(data.event.id, true);
                      setGiven({ id: data.event.id, relevant: true });
                    }}
                  >
                    Relevant
                  </Button>
                  <Button
                    variant="danger"
                    onClick={async () => {
                      await api.feedback(data.event.id, false);
                      setGiven({ id: data.event.id, relevant: false });
                    }}
                  >
                    Not relevant
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-xs text-[var(--color-muted)]">
                {feedback ? 'Marked relevant — thanks.' : 'Marked not relevant — thanks.'}
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
